import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronRight, Grid, MoreHorizontal, User, RotateCcw, RotateCw } from "react-feather";
import { ChevronDownIcon } from "../../atoms/ChevronDownIcon";
import { Tooltip } from "../../atoms/Tooltip";
import { Switch } from "../../atoms/Switch";
import { StoryCollaboratorsList } from "../StoryCollaboratorsList";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { rawColors } from "../../../design-system/tokens/colors";
import {
  Container,
  ProfilePill,
  Avatar,
  SharePill,
  ShareText,
  KebabButton,
  RefreshTrigger,
  NavMenuPortal,
  NavMenuList,
  NavMenuItem,
  NavMenuIcon,
  ShareMenuPortal,
  ShareMenuItem,
  ShareIconTile,
  ShareMenuDivider,
  NavMenuControlIcon,
  NavMenuSwitch,
  NavMenuText,
  NavMenuLabelRow,
  NavMenuBadge,
  NavMenuDescription,
  NavMenuNote,
  NavMenuNoteSegment,
  NavMenuTrailing,
  HistoryGroup,
  HistoryButton,
  OverflowChip,
  AvatarImg,
} from "./StoryHeaderActions.styles";
import type { StoryHeaderActionsProps } from "./StoryHeaderActions.types";

/** Icons a menu row can ask for by name. */
const NAV_ICONS = { grid: <Grid size={24} aria-hidden /> } as const;

const MAX_AVATARS = 3;

/** Up to two leading letters of a name, upper-cased. */
const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

/**
 * Story-detail header right-side actions (Figma node 3812:41996):
 * undo/redo · refresh icon · collaborator avatars · primary Share pill · overflow button.
 * The status menu lives on the title chip (StoryHeaderTitle).
 *
 * Stateless molecule — open/menu state and real data are owned by the parent.
 * Handlers are optional; unwired buttons are inert (stubbed).
 */
export const StoryHeaderActions: React.FC<StoryHeaderActionsProps> = ({
  bellSlot,
  history,
  collaboratorCount = 1,
  onMoreClick,
  moreMenuOpen = false,
  onMoreMenuToggle,
  moreItems,
  onMoreSelect,
  showStatusAndRefresh = false,
  refreshDropdownOpen = false,
  onRefreshDropdownToggle,
  refreshOptions,
  onRefreshSelect,
  onRefreshMenuAnchorChange,
  isShareEnabled = true,
  shareDropdownOpen = false,
  onShareDropdownToggle,
  shareOptions,
  onShareSelect,
  profileDropdownOpen = false,
  onProfileDropdownToggle,
  collaborators,
  collaboratorsLoading,
  collaboratorsError,
  profileOptions,
  onProfileSelect,
}) => {
  const {
    triggerRef: refreshTriggerRef,
    menuRef: refreshMenuRef,
    rect: refreshRect,
  } = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: refreshDropdownOpen,
    onOutsideClick: onRefreshDropdownToggle,
  });

  useEffect(() => {
    if (!refreshRect) return;
    onRefreshMenuAnchorChange?.({
      top: refreshRect.top,
      bottom: refreshRect.bottom,
      right: window.innerWidth - refreshRect.left - refreshRect.width,
    });
  }, [onRefreshMenuAnchorChange, refreshRect]);

  const {
    triggerRef: shareTriggerRef,
    menuRef: shareMenuRef,
    rect: shareRect,
  } = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: shareDropdownOpen,
    onOutsideClick: onShareDropdownToggle,
  });

  const {
    triggerRef: profileTriggerRef,
    menuRef: profileMenuRef,
    rect: profileRect,
  } = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: profileDropdownOpen,
    onOutsideClick: onProfileDropdownToggle,
  });

  const {
    triggerRef: moreTriggerRef,
    menuRef: moreMenuRef,
    rect: moreRect,
  } = usePortalMenu<HTMLButtonElement, HTMLUListElement>({
    open: moreMenuOpen,
    onOutsideClick: onMoreMenuToggle,
  });

  // Keep the ⋯ menu inside the viewport gutter: it hangs from the trigger's right edge, and when it is wider than
  // the room to the left of that edge it slides right (never past the gutter) instead of running off the screen.
  const [moreShift, setMoreShift] = useState(0);
  useLayoutEffect(() => {
    const menu = moreMenuRef.current;
    if (!moreMenuOpen || !menu || !moreRect) {
      setMoreShift(0);
      return;
    }
    // 1rem; an environment without a computed root font size (jsdom) would make the shift NaN.
    const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const gutter = Number.isFinite(rootFont) ? rootFont : 16;
    const right = window.innerWidth - moreRect.left - moreRect.width;
    const left = window.innerWidth - right - menu.offsetWidth;
    const slide = Math.max(0, gutter - left);
    setMoreShift(Math.min(slide, Math.max(0, right - gutter)));
  }, [moreMenuOpen, moreRect, moreItems, moreMenuRef]);

  const total = collaborators?.length ?? collaboratorCount;
  const avatars: { id: string; name?: string; avatarUrl?: string | null }[] =
    collaborators?.slice(0, MAX_AVATARS) ??
    Array.from({ length: Math.max(1, Math.min(total, MAX_AVATARS)) }, (_, i) => ({ id: `placeholder-${i}` }));
  const overflowCount = Math.max(0, total - MAX_AVATARS);

  return (
    <Container>
      {bellSlot}
      {showStatusAndRefresh && (
        <HistoryGroup>
          <HistoryButton
            type="button"
            aria-label="Undo"
            disabled={!history?.canUndo}
            onClick={() => void history?.undo()}
          >
            <RotateCcw size={16} />
          </HistoryButton>
          <HistoryButton
            type="button"
            aria-label="Redo"
            disabled={!history?.canRedo}
            onClick={() => void history?.redo()}
          >
            <RotateCw size={16} />
          </HistoryButton>
        </HistoryGroup>
      )}
      <ProfilePill
        ref={profileTriggerRef}
        type="button"
        onClick={onProfileDropdownToggle}
        aria-haspopup="listbox"
        aria-expanded={profileDropdownOpen}
        aria-label="Collaborators"
      >
        {avatars.map((c) => (
          <Avatar key={c.id} aria-hidden title={c.name}>
            {c.avatarUrl ? (
              <AvatarImg src={c.avatarUrl} alt="" />
            ) : c.name ? (
              initialsOf(c.name)
            ) : (
              <User size={16} />
            )}
          </Avatar>
        ))}
        {overflowCount > 0 ? <OverflowChip aria-hidden>+{overflowCount}</OverflowChip> : null}
      </ProfilePill>

      {profileDropdownOpen &&
        (collaborators || profileOptions) &&
        profileRect &&
        createPortal(
          <NavMenuPortal
            ref={profileMenuRef}
            role="listbox"
            style={{
              top: profileRect.top,
              bottom: profileRect.bottom,
              right: window.innerWidth - profileRect.left - profileRect.width,
            }}
          >
            {collaborators ? (
              <StoryCollaboratorsList
                collaborators={collaborators}
                isLoading={collaboratorsLoading}
                errorMessage={collaboratorsError}
              />
            ) : (
              profileOptions?.map((opt) => (
                <NavMenuList key={opt.value}>
                  <NavMenuItem
                    role="option"
                    onClick={() => {
                      onProfileSelect?.(opt.value);
                      onProfileDropdownToggle?.();
                    }}
                  >
                    {opt.icon && <NavMenuIcon>{opt.icon}</NavMenuIcon>}
                    {opt.label}
                  </NavMenuItem>
                </NavMenuList>
              ))
            )}
          </NavMenuPortal>,
          document.body,
        )}

      {showStatusAndRefresh && (
        <RefreshTrigger
          ref={refreshTriggerRef}
          type="button"
          onClick={onRefreshDropdownToggle}
          aria-haspopup="listbox"
          aria-expanded={refreshDropdownOpen}
          aria-label="Refresh options"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <path
              d="M3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.516 3.00947 16.931 3.99122 18.74 5.74L21 8"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 3V8H16"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 12C21 14.3869 20.0518 16.6761 18.364 18.364C16.6761 20.0518 14.3869 21 12 21C9.48395 20.9905 7.06897 20.0088 5.26 18.26L3 16"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 16H3V21"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </RefreshTrigger>
      )}

      {refreshDropdownOpen &&
        refreshOptions &&
        refreshRect &&
        createPortal(
          <NavMenuPortal
            ref={refreshMenuRef}
            role="listbox"
            style={{
              top: refreshRect.top,
              bottom: refreshRect.bottom,
              right: window.innerWidth - refreshRect.left - refreshRect.width,
            }}
          >
            {refreshOptions.map((opt) => (
              <NavMenuList key={opt.value}>
                <NavMenuItem
                  role="option"
                  onClick={() => {
                    if (opt.switchValue !== undefined) return;
                    // onRefreshSelect closes the dropdown itself
                    // (handleRefreshSelect) — toggling here too would race
                    // the same-event state update and reopen it.
                    onRefreshSelect?.(opt.value);
                  }}
                >
                  {opt.switchValue !== undefined && (
                    <NavMenuSwitch onClick={(event) => event.stopPropagation()}>
                      <Switch
                        value={opt.switchValue}
                        onChange={(value) => opt.onSwitchChange?.(value)}
                        size="sm"
                        ariaLabel={opt.label}
                      />
                    </NavMenuSwitch>
                  )}
                  {opt.icon && (
                    <NavMenuControlIcon>{opt.icon}</NavMenuControlIcon>
                  )}
                  {opt.label}
                </NavMenuItem>
              </NavMenuList>
            ))}
          </NavMenuPortal>,
          document.body,
        )}

      {isShareEnabled ? (
        <SharePill
          ref={shareTriggerRef}
          type="button"
          onClick={onShareDropdownToggle}
          aria-haspopup="listbox"
          aria-expanded={shareDropdownOpen}
          aria-label="Share"
        >
          <ShareText>Share</ShareText>
          <ChevronDownIcon color={rawColors.text.white} />
        </SharePill>
      ) : (
        <Tooltip label="Available once your story finishes generating" position="bottom">
          <SharePill type="button" disabled aria-label="Share" aria-disabled>
            <ShareText>Share</ShareText>
            <ChevronDownIcon color={rawColors.text.white} />
          </SharePill>
        </Tooltip>
      )}

      {shareDropdownOpen &&
        shareOptions &&
        shareRect &&
        createPortal(
          <ShareMenuPortal
            ref={shareMenuRef}
            role="listbox"
            style={{
              top: shareRect.top,
              bottom: shareRect.bottom,
              right: window.innerWidth - shareRect.left - shareRect.width,
            }}
          >
            {shareOptions.map((opt) => {
              const icon = opt.icon ?? (opt.iconName ? NAV_ICONS[opt.iconName] : null);
              const description = opt.disabled && opt.disabledNote ? opt.disabledNote : opt.description;
              const note = opt.disabled
                ? null
                : opt.noteSegments ?? (opt.note ? [{ text: opt.note }] : opt.emptyNote ? [{ text: opt.emptyNote }] : null);
              return (
                <NavMenuList key={opt.value}>
                  {opt.dividerBefore && <ShareMenuDivider role="separator" />}
                  <ShareMenuItem
                    role="option"
                    aria-disabled={opt.disabled || undefined}
                    $highlight={opt.highlight && !opt.disabled}
                    $disabled={opt.disabled}
                    onClick={() => {
                      if (opt.disabled) return;
                      // onShareSelect closes the dropdown itself
                      // (handleCollaborate/handleDeliverStory) — toggling
                      // here too would race the same-event state update and
                      // reopen it (setShareDropdownOpen(false) then prev=>!prev
                      // resolves against that false, flipping back to true).
                      onShareSelect?.(opt.value);
                    }}
                  >
                    {icon && <ShareIconTile $tone={opt.iconTone}>{icon}</ShareIconTile>}
                    {description || opt.badge || note ? (
                      <NavMenuText>
                        <NavMenuLabelRow>
                          {opt.label}
                          {opt.badge && <NavMenuBadge $solid={opt.highlight}>{opt.badge}</NavMenuBadge>}
                        </NavMenuLabelRow>
                        {description && <NavMenuDescription>{description}</NavMenuDescription>}
                        {note && (
                          <NavMenuNote key={note.map((segment) => segment.text).join("|")}>
                            {note.map((segment, index) => (
                              <React.Fragment key={segment.text}>
                                {index > 0 && " · "}
                                <NavMenuNoteSegment $attention={segment.tone === "attention"}>{segment.text}</NavMenuNoteSegment>
                              </React.Fragment>
                            ))}
                          </NavMenuNote>
                        )}
                      </NavMenuText>
                    ) : (
                      opt.label
                    )}
                    {(opt.trailing || (opt.highlight && !opt.disabled)) && (
                      <NavMenuTrailing>{opt.trailing ?? <ChevronRight aria-hidden />}</NavMenuTrailing>
                    )}
                  </ShareMenuItem>
                </NavMenuList>
              );
            })}
          </ShareMenuPortal>,
          document.body,
        )}

      <KebabButton
        ref={moreTriggerRef}
        type="button"
        onClick={onMoreMenuToggle ?? onMoreClick}
        aria-haspopup={moreItems ? "menu" : undefined}
        aria-expanded={moreItems ? moreMenuOpen : undefined}
        aria-label="More options"
      >
        <MoreHorizontal size={18} />
      </KebabButton>

      {moreMenuOpen &&
        moreItems &&
        moreItems.length > 0 &&
        moreRect &&
        createPortal(
          <NavMenuPortal
            ref={moreMenuRef}
            role="menu"
            style={{
              top: moreRect.top,
              bottom: moreRect.bottom,
              right: window.innerWidth - moreRect.left - moreRect.width - moreShift,
              maxWidth: "calc(100vw - 2rem)",
            }}
          >
            {moreItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavMenuList key={item.id}>
                  <NavMenuItem
                    role="menuitem"
                    tabIndex={0}
                    aria-disabled={item.disabled || undefined}
                    onClick={() => {
                      if (item.disabled) return;
                      onMoreSelect?.(item.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (!item.disabled) onMoreSelect?.(item.id);
                        return;
                      }
                      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
                      event.preventDefault();
                      const rows = Array.from(
                        moreMenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
                      );
                      const at = rows.indexOf(event.currentTarget);
                      const step = event.key === "ArrowDown" ? 1 : -1;
                      rows[(at + step + rows.length) % rows.length]?.focus();
                    }}
                  >
                    {Icon && (
                      <NavMenuIcon>
                        <Icon size={16} />
                      </NavMenuIcon>
                    )}
                    {item.label}
                  </NavMenuItem>
                </NavMenuList>
              );
            })}
          </NavMenuPortal>,
          document.body,
        )}
    </Container>
  );
};

export default StoryHeaderActions;
